import { FC } from 'react';

// Third-party
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import { InputBase } from '@mui/material';
import Box from '@mui/material/Box';
import { useController, useFormContext } from 'react-hook-form';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

// Types
import { Message } from 'react-hook-form/dist/types/errors';
import { useTranslation } from 'react-i18next';
import { BASE_COLORS } from '@mgm/common/utils';
import { TCreateEditTasks } from '../type';

// Utils
import { draggableReorder } from '../../../../utils/helpers';

// Components
import { DraggableTasks } from './DraggableTasks';
import { StrictModeDroppable } from '../../../../components/StrictModeDroppable';

const CreateEditTasks: FC<TCreateEditTasks> = ({
  tasks,
  addTask,
  deleteTask,
  rules = {},
  onTaskDrag,
}) => {
  const {
    control,
    formState: { errors },
    resetField,
  } = useFormContext();
  const { t } = useTranslation('tasklists');

  const {
    field: { ref, ...inputProps },
  } = useController({
    control,
    name: 'task',
    rules: {
      validate: {
        ...rules,
      },
    },
    defaultValue: '',
  });

  const handleTask = () => {
    addTask(inputProps.value);
    resetField('task');
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const newTasks = draggableReorder(
      tasks,
      result.source.index,
      result.destination.index,
    );

    onTaskDrag(newTasks);
  };

  return (
    <Card
      component={Paper}
      sx={{
        width: '100%',
        bgcolor: 'background.paper',
        overflow: 'auto',
        height: 250,
      }}
    >
      <Box padding={1}>
        <InputBase
          {...inputProps}
          id="component-helper-text"
          inputRef={ref}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === 'Enter' && inputProps.value !== '') {
              if (!(!!errors.task && errors?.task?.message)) {
                handleTask();
              }
            }
          }}
          sx={{ ml: 1, flex: 1 }}
          size="small"
          error={!!errors.task}
          placeholder={t('writeTask')}
          inputProps={{
            style: { padding: 0 },
          }}
          endAdornment={(
            <InputAdornment
              sx={{
                mr: 2.5,
              }}
              position="end"
            >
              {inputProps.value !== '' && (
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTask}
                  edge="end"
                >
                  <AddCircleOutlineRoundedIcon fontSize="small" color="primary" />
                </IconButton>
              )}
            </InputAdornment>
            )}
        />
        {!!errors.task && errors?.task?.message && (
        <FormHelperText
          sx={{
            color: BASE_COLORS.red,
            pl: 1,
          }}
        >
          {errors?.task?.message as Message || ''}
        </FormHelperText>
        )}
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <DragDropContext onDragEnd={onDragEnd}>
        <StrictModeDroppable droppableId="list">
          {(provided) => (
            <div
              className="draggable-list-wrapper"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <DraggableTasks onDeleteTask={deleteTask} tasks={tasks} />
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </DragDropContext>
    </Card>
  );
};

export {
  CreateEditTasks,
};
